import { Collection, Database, Q } from '@nozbe/watermelondb';

import Organisation from './Organisation';
import {
    HorseContactModel,
    HorseContactPayload,
} from '../../types/HorseContact';
import { DBServiceOptionsWithImages } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import { addPreparedDataBatch, mapRecord } from './helpers/horseContact/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddHorseContactAction,
    prepareCreateHorseContact,
} from './helpers/horseContact/horseContactAdd';
import {
    logDeleteHorseContactAction,
    prepareDeleteHorseContact,
} from './helpers/horseContact/horseContactDelete';

type HorseContactDBServiceOptions = DBServiceOptionsWithImages & {
    validation: DatabaseValidation;
};

class HorseContact {
    private database: Database;
    private collection: Collection<HorseContactModel>;
    private table = 'horse_contacts';
    private options: HorseContactDBServiceOptions;

    constructor(options: HorseContactDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async add(payload: HorseContactPayload, userId: string) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        await this.database.write(async () => {
            const createdHorseContact = prepareCreateHorseContact({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });

            const preparedData = [
                mapRecord(createdHorseContact, 'horse_contacts', 'add'),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddHorseContactAction({
                horseContact: createdHorseContact,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }

    async deleteByID(id: string) {
        const horseContactElement = await this.getByID(id);

        await this.database.write(async () => {
            const deletedHorseContact = prepareDeleteHorseContact({
                horseContact: horseContactElement,
            });

            const preparedData = [
                mapRecord(deletedHorseContact, 'horse_contacts', 'delete'),
            ];

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logDeleteHorseContactAction({
                horseContact: deletedHorseContact,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }
}

export default HorseContact;
